import { useContext, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetch } from 'loti-request';
import classNames from 'classnames';

import { getMatchesFetchUrl } from 'app/utils/entities/tournament';
import ConfigContext from 'app/common/ConfigContext';
import { formatOrdinals } from 'app/i18n/utils';
import { getVideogameTitle } from 'app/config/videogameSpecific.config.helper';
import InfoIcon from 'app/icons/info.svg';

import StandingTable from './StandingTable';
import BracketMatchesList from './BracketMatchesList';
import style from './style.module.scss';
import { getPlaceholderMatches, getPlaceholderStandings } from './utils';

interface Props {
  bracket: TournamentBracketWithStandings;
  withTabs?: boolean;
  matchesListCollapsedByDefault?: boolean;
}

const BracketWithStandings: FC<Props> = ({
  bracket,
  withTabs = false,
  matchesListCollapsedByDefault
}) => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const { lang } = config;
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  const { data: matchesData } = useFetch<TournamentBracketMatch[]>({
    url: getMatchesFetchUrl(bracket.id, config)
  });

  const matchesToShow =
    selectedTeamId && matchesData
      ? matchesData.filter(match =>
          match.opponents.some(opponent => opponent.id === selectedTeamId)
        )
      : matchesData ?? [];

  const withStandingDisclaimerMessage =
    bracket.main_standings.length === 0 && bracket.tiebreaks.length === 0;

  return (
    <div className={style.bracketStandings}>
      <div>
        <StandingTable
          selectTeam={setSelectedTeamId}
          selectedTeamId={selectedTeamId}
          standings={
            bracket.main_standings.length === 0 ? getPlaceholderStandings() : bracket.main_standings
          }
          title={t('blocks_bracket_standings_main_title')}
          type="main"
          // matches={matchesData ?? []}
          // videogame={bracket.videogame_slug}
          // videogameTitle={getVideogameTitle(bracket.videogame_title)}
        />
        {bracket.tiebreaks.map((tiebreak, index) => (
          <StandingTable
            key={index}
            selectTeam={setSelectedTeamId}
            selectedTeamId={selectedTeamId}
            standings={tiebreak.standings}
            title={t('blocks_bracket_standings_tiebreak_title', {
              position: formatOrdinals(lang, tiebreak.tiebreaker_position)
            })}
            type="tiebreak"
            // matches={matchesData ?? []}
            // videogame={bracket.videogame_slug}
            // videogameTitle={getVideogameTitle(bracket.videogame_title)}
          />
        ))}
      </div>
      <div className={style.matchesListContainer}>
        <div className={classNames(style.matchesList, { [style.withTabs]: withTabs })}>
          <BracketMatchesList
            matches={matchesToShow.length === 0 ? getPlaceholderMatches() : matchesToShow}
            videogame={bracket.videogame_slug}
            videogameTitle={getVideogameTitle(bracket.videogame_title)}
            collapsedByDefault={matchesListCollapsedByDefault}
            selectedTeamId={selectedTeamId}
          />
        </div>
      </div>
      {withStandingDisclaimerMessage && (
        <div className={style.standingDisclaimerMessageContainer}>
          <p className={style.standingDisclaimerMessage}>
            <InfoIcon className={style.infoIcon} />
            <span>{t('component-brackets-disclaimer')}</span>
          </p>
        </div>
      )}
    </div>
  );
};

BracketWithStandings.displayName = 'BracketWithStandings';

export default BracketWithStandings;
